import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

import { includesRetiredSpouse } from '../../../utils'

const CATCH_UP_ELIGIBLE_PLAN_TYPES = [1, 2] // 401(k), 403(b)
const ATMOS_TEMPLATE_ID = 4 // Suppresses the expected lump-sum payout message

/*
 * The placeholder salary we write to the database when the plan provider sends
 * us nothing to work with (Paychex sends $0, and the engine will not run without
 * a salary). Matched EXACTLY, not as a floor: someone who genuinely earns
 * $10,000 is rare, someone who earns less is not, and treating "under $10,000"
 * as the placeholder would tell every one of them their income is wrong.
 */
export const DEFAULT_ANNUAL_SALARY = 10000

/**
 * Whether the participant's pre-tax annual income is still our placeholder.
 *
 * Shared with QuickActions, which swaps a card on the same condition, so the
 * two surfaces can't drift apart on what counts as "not a real salary".
 */
export const usesDefaultSalary = account => account?.annualSalary === DEFAULT_ANNUAL_SALARY

const ACTION_ITEM_DISPLAY_ORDER = [
  'add at least one account',
  'reviewIncomeDefaultSalary',
  'ageEqualOrGreaterThanRetirement',
  'spouseReachedRetirementAge',
  'lumpSumPensionPayoutExpected',
  'lumpSumPensionMayBeEligible',
  'reviewRetirementAccountThatWasAddedByInstitution',
  'catchUp50to59',
  'catchUp60to63',
  'catchUpOver64',
]

/**
 * Returns the active accumulation-mode action item keys in display order,
 * or ['all clear'] when none apply.
 */
export default function useActionItems({
  mode,
  person,
  account,
  isRetail,
  templateId,
  enableCatchupContributions,
  employerAccounts,
  otherAccounts,
  institutionalAccount,
  isRequiredToReviewRetirementAccount,
  getAllPensions,
  spouse,
  userLogs,
}) {
  const [lumpSumKey, setLumpSumKey] = useState(null)
  const [spouseRetiredSinceLastLogin, setSpouseRetiredSinceLastLogin] = useState(false)

  useEffect(() => {
    let active = true
    const resolveLumpSum = async () => {
      const pensions = await getAllPensions()
      const expectsLumpSum = pensions.some(
        pension =>
          pension.paymentType === 'L' &&
          pension.lumpsumreceived === 'N' &&
          pension.pensionStartAge <= person.age
      )
      if (!active) return
      if (expectsLumpSum) {
        setLumpSumKey(templateId === ATMOS_TEMPLATE_ID ? null : 'lumpSumPensionPayoutExpected')
      } else if (account.employeeStatus !== 1 && account.employeeStatus !== 2) {
        setLumpSumKey('lumpSumPensionMayBeEligible')
      } else {
        setLumpSumKey(null)
      }
    }
    resolveLumpSum().catch(err => console.error(err))
    return () => {
      active = false
    }
  }, [getAllPensions, person.age, account.employeeStatus, templateId])

  /*
   * Whether the spouse crossed their retirement age while the user was away.
   *
   * The alert exists because the goal was set against two incomes and now
   * covers one, and the drop is easy to miss on a screen the user has seen many
   * times. Scoping it to "since your last login" is also what keeps it from
   * nagging: the window it fires in has passed by the next visit, so it shows
   * once. A spouse who is not included in planning never contributed to the
   * goal, so nothing changed for them.
   */
  const spouseRetirementDate = includesRetiredSpouse({ person, spouse })
    ? spouse.retirementDate
    : null

  useEffect(() => {
    if (!spouseRetirementDate || !userLogs) return undefined

    let active = true
    const resolveSpouseRetirement = async () => {
      await userLogs.getLoginEventType()
      if (!active) return
      const previousLogin = userLogs.previousLoginTimestamp
      // No earlier login means no "last time" to compare against — a first-ever
      // visit, or a login log we could not read.
      setSpouseRetiredSinceLastLogin(
        Boolean(previousLogin) && spouseRetirementDate.isAfter(dayjs(previousLogin))
      )
    }
    resolveSpouseRetirement().catch(err => console.error(err))
    return () => {
      active = false
    }
    // `spouseRetirementDate` is a fresh dayjs on every render; compare its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spouseRetirementDate?.valueOf(), userLogs])

  // Read outside the memo so its dependency is the boolean, not the `account`
  // node itself — MST hands back a fresh proxy identity on any change to it.
  const salaryIsDefault = usesDefaultSalary(account)

  const activeKeys = useMemo(() => {
    const keys = new Set()

    if (isRetail && otherAccounts.length === 0 && employerAccounts.length === 0) {
      keys.add('add at least one account')
    }

    if (salaryIsDefault) {
      keys.add('reviewIncomeDefaultSalary')
    }

    if (mode === 'retWithinThreeYears' && person.age >= person.retirementAge) {
      keys.add('ageEqualOrGreaterThanRetirement')
    }

    if (spouseRetiredSinceLastLogin) {
      keys.add('spouseReachedRetirementAge')
    }

    if (lumpSumKey) {
      keys.add(lumpSumKey)
    }

    if (institutionalAccount && isRequiredToReviewRetirementAccount) {
      keys.add('reviewRetirementAccountThatWasAddedByInstitution')
    }

    const hasCatchUpEligibleAccount = employerAccounts.some(a =>
      CATCH_UP_ELIGIBLE_PLAN_TYPES.includes(a.planType)
    )
    const catchUpAge = dayjs().year() - dayjs(person.birthDate).year()
    if (enableCatchupContributions && catchUpAge >= 50 && hasCatchUpEligibleAccount) {
      if (catchUpAge < 60) keys.add('catchUp50to59')
      else if (catchUpAge < 64) keys.add('catchUp60to63')
      else keys.add('catchUpOver64')
    }

    const ordered = ACTION_ITEM_DISPLAY_ORDER.filter(key => keys.has(key))
    return ordered.length > 0 ? ordered : ['all clear']
  }, [
    mode,
    person.age,
    person.birthDate,
    person.retirementAge,
    salaryIsDefault,
    isRetail,
    enableCatchupContributions,
    employerAccounts,
    otherAccounts.length,
    institutionalAccount,
    isRequiredToReviewRetirementAccount,
    lumpSumKey,
    spouseRetiredSinceLastLogin,
  ])

  return activeKeys
}
